import React, { useState, useEffect, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isToday,
  isSameMonth,
} from "date-fns";
import { cs } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";

//
// ────────────────────────────────────────────────
//   České státní svátky + výpočet Velikonoc
// ────────────────────────────────────────────────
//
function getEasterDate(year) {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);
  return new Date(year, month - 1, day);
}

function getCzechHolidays(year) {
  const easter = getEasterDate(year);

  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  return [
    new Date(year, 0, 1),
    new Date(year, 4, 1),
    new Date(year, 4, 8),
    new Date(year, 6, 5),
    new Date(year, 6, 6),
    new Date(year, 8, 28),
    new Date(year, 9, 28),
    new Date(year, 10, 17),
    new Date(year, 11, 24),
    new Date(year, 11, 25),
    new Date(year, 11, 26),
    goodFriday,
    easterMonday,
  ];
}

// mapování typů volna na české popisky
const leaveTypeLabels = {
  dovolena: "Dovolená",
  lekar: "Lékař",
  "nahradni-volno": "Náhradní volno",
  nv: "Náhradní volno",
  prekazka: "Překážka",
  nemoc: "Nemoc",

  // směny
  ranni: "Ranní směna",
  odpo: "Odpolední směna",
  celodenni: "Celodenní směna",
};


// Statusy, které se mají zobrazovat v kalendáři
function isApprovedLeaveStatus(status) {
  // Oprava je efektivní → zobrazit jako schválené
  // Zrušení je administrativní → nezobrazovat jako event (a do schválených to nepatří)
  return status === "schváleno" || status === "schváleno (oprava)";
}

function isPendingLeaveStatus(status) {
  return status === "čeká";
}

//
// ────────────────────────────────────────────────
//   Tooltip – FIXED + near cursor + no overflow
// ────────────────────────────────────────────────
//
const Tooltip = ({ tooltip, cursor }) => {
  if (!tooltip) return null;

  const { type, data, day } = tooltip;

  const width = 260;
  const offset = 20;

  const top =
    cursor.y + 180 > window.innerHeight
      ? cursor.y - 140
      : cursor.y + offset;

  const left =
    cursor.x + width + 40 > window.innerWidth
      ? cursor.x - width - 20
      : cursor.x + offset;

  const dayLabel = day ? format(day, "d.M.yyyy") : null;

  return (
    <div
      className="
        fixed bg-white border border-gray-300 shadow-lg rounded-md p-3 
        text-sm z-50 max-w-xs
      "
      style={{ top, left, width }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hlavička */}
      <div className="font-semibold mb-1 flex items-center gap-1">
        {type === "tasks" && <span>📌</span>}
        {type === "note" && <span>📝</span>}
        {type === "leaves" && <span>🗓️</span>}
        {type === "tasks" && <span>Úkoly</span>}
        {type === "note" && <span>Poznámka</span>}
        {type === "leaves" && <span>Události dne</span>}
        {dayLabel && (
          <span className="text-xs text-gray-500 ml-1">({dayLabel})</span>
        )}
      </div>

      <div className="border-t border-gray-200 my-1"></div>

      {/* Obsah podle typu */}
      {type === "tasks" && (
        <div className="space-y-1">
          {data.map((t) => (
            <div key={t._id}>• {t.title}</div>
          ))}
        </div>
      )}

      {type === "note" && <div>{data.text}</div>}

      {type === "leaves" && (
        <div className="space-y-2">
          {/* Schválené */}
          {data.filter((e) => isApprovedLeaveStatus(e.status)).length > 0 && (
            <>
              <div className="text-xs font-semibold text-green-700">Schválené</div>
              {data
                .filter((e) => isApprovedLeaveStatus(e.status))
                .map((e) => (
                  <div key={e.id} className="text-xs leading-snug">
                    <span className="text-green-600 mr-1">●</span>
                    {e.employeeName} – {e.label}{" "}
                    {e.hoursLabel && (
                      <span className="text-gray-700">({e.hoursLabel})</span>
                    )}
                    {e.note && (
                      <div className="text-gray-500 ml-4">
                        Poznámka: {e.note}
                      </div>
                    )}
                  </div>
                ))}
            </>
          )}

          {/* Čekající */}
          {data.filter((e) => isPendingLeaveStatus(e.status)).length > 0 && (
            <>
              {data.filter((e) => isApprovedLeaveStatus(e.status)).length > 0 && (
                <div className="border-t border-gray-200 my-1" />
              )}
              <div className="text-xs font-semibold text-yellow-700">Čekající</div>
              {data
                .filter((e) => isPendingLeaveStatus(e.status))
                .map((e) => (
                  <div key={e.id} className="text-xs leading-snug">
                    <span className="text-yellow-500 mr-1">●</span>
                    {e.employeeName} – {e.label}{" "}
                    {e.hoursLabel && (
                      <span className="text-gray-700">({e.hoursLabel})</span>
                    )}
                    {e.note && (
                      <div className="text-gray-500 ml-4">
                        Poznámka: {e.note}
                      </div>
                    )}
                  </div>
                ))}
            </>
          )}

          {/* Pokud nejsou ani schválené ani čekající */}
          {data.filter(
            (e) => isApprovedLeaveStatus(e.status) || isPendingLeaveStatus(e.status)
          ).length === 0 && (
            <div className="text-xs text-gray-500">Žádné relevantní události.</div>
          )}
        </div>
      )}
    </div>
  );
};

//
// ────────────────────────────────────────────────
//   Hlavní komponenta
// ────────────────────────────────────────────────
//
export default function BranchCalendarPage() {
  const { user } = useAuth();
  const pharmacyCode = user?.pharmacyCode;
  const firma = user?.firma;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [leaveRequests, setLeaveRequests] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});

  const [tooltip, setTooltip] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [noteText, setNoteText] = useState("");

  // modal záložky: poznámka vs úkol
  const [modalTab, setModalTab] = useState("note"); // "note" | "task"

  // fields pro nový úkol
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskRemindAt, setTaskRemindAt] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // sledování pozice kurzoru pro tooltip
  useEffect(() => {
    const move = (e) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  const showTooltip = (e, day, type, data) => {
    setTooltip({ type, data, day });
  };

  const hideTooltip = () => setTooltip(null);

  //
  // ────────────────────────────────────────────────
  //   Kalendářní výpočty
  // ────────────────────────────────────────────────
  //
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  let days = [];
  let d = calendarStart;
  while (d <= calendarEnd) {
    days.push(d);
    d = addDays(d, 1);
  }

  const holidays = useMemo(() => {
    const y = currentMonth.getFullYear();
    return getCzechHolidays(y).map((d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    );
  }, [currentMonth]);

  const getMonthParam = () => format(currentMonth, "yyyy-MM");

  //
  // ────────────────────────────────────────────────
  //   Načíst poznámky
  // ────────────────────────────────────────────────
  //
  useEffect(() => {
    if (!pharmacyCode) return;

    const loadNotes = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `/api/calendar/${pharmacyCode}?month=${getMonthParam()}`,
          { credentials: "include" }
        );

        if (!res.ok) throw new Error("Nepodařilo se načíst poznámky.");

        setNotes(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [pharmacyCode, currentMonth]);

  //
  // ────────────────────────────────────────────────
  //   Načíst úkoly
  // ────────────────────────────────────────────────
  //
  const loadTasks = async () => {
    try {
      const res = await fetch("/api/tasks/my", { credentials: "include" });
      if (!res.ok) return;
      setTasks(await res.json());
    } catch (err) {
      console.error("Error loading tasks:", err);
    }
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //
  // ────────────────────────────────────────────────
  //   Načíst zaměstnance (kvůli dennímu fondu)
  // ────────────────────────────────────────────────
  //
  useEffect(() => {
    if (!firma || !pharmacyCode) return;

    const loadEmployees = async () => {
      try {
        const res = await fetch(
          `/api/employees/${firma}/${pharmacyCode}`,
          { credentials: "include" }
        );
        if (!res.ok) return;
        const arr = await res.json();
        const map = {};
        arr.forEach((emp) => {
          map[emp._id] = emp; // obsahuje např. denniFond, fullName atd.
        });
        setEmployeeMap(map);
      } catch (err) {
        console.error("Error loading employees:", err);
      }
    };

    loadEmployees();
  }, [firma, pharmacyCode]);

  //
  // ────────────────────────────────────────────────
  //   Načíst žádosti o volno (LeaveRequests)
  // ────────────────────────────────────────────────
  //
  useEffect(() => {
    if (!firma || !pharmacyCode) return;

    const loadLeaves = async () => {
      try {
        const res = await fetch(
          `/api/leaves/${firma}/${pharmacyCode}`,
          { credentials: "include" }
        );
        if (!res.ok) return;
        const data = await res.json();
        setLeaveRequests(data);
      } catch (err) {
        console.error("Error loading leaves:", err);
      }
    };

    loadLeaves();
  }, [firma, pharmacyCode]);

  //
  // ────────────────────────────────────────────────
  //   Helpery
  // ────────────────────────────────────────────────
  //
  const getNoteForDay = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return notes.find((n) => n.date === formatted);
  };

  const fmtDateCz = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("cs-CZ");
  };

  const myKey = pharmacyCode ? String(pharmacyCode).trim() : null;

  const getEffectiveTaskDate = (t) => {
    const snoozeUntil = myKey ? t?.snoozes?.[myKey]?.until : null;
    return snoozeUntil || t?.remindAt || t?.dueDate || null;
  };

  const taskHasReminder = (t) => !!t?.remindAt;

  const taskIsSnoozed = (t) => !!(myKey && t?.snoozes?.[myKey]?.until);

  const getLeaveEventsForDay = (day) => {
    const target = format(day, "yyyy-MM-dd");
    const events = [];

    leaveRequests.forEach((req) => {
      // Zajímají nás jen efektivní schválené + čekající
      if (!isApprovedLeaveStatus(req.status) && !isPendingLeaveStatus(req.status)) return;    

      (req.days || []).forEach((dItem, index) => {
        const dDate = format(new Date(dItem.date), "yyyy-MM-dd");
        if (dDate !== target) return;

        const typeKey = dItem.type;
        const label = leaveTypeLabels[typeKey] || typeKey;

        const emp = employeeMap[req.employeeId];
        const denniFond = emp?.denniFond;

        let hoursLabel = "";
        if (typeof dItem.hours === "number") {
          if (
            typeof denniFond === "number" &&
            Math.abs(dItem.hours - denniFond) < 0.01
          ) {
            hoursLabel = "celý den";
          } else {
            hoursLabel = `${dItem.hours} hod`;
          }
        }

        events.push({
          id: `${req._id}-${index}`,
          employeeName: req.employeeName,
          status: req.status, // "schváleno" / "čeká" / "schváleno (oprava)"
          type: typeKey,
          label,
          hours: dItem.hours,
          hoursLabel,
          note: dItem.note,
        });
      });
    });

    return events;
  };

  //
  // ────────────────────────────────────────────────
  //   Modal: otevřít / uložit / smazat poznámku
  // ────────────────────────────────────────────────
  //
  const openModal = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    setSelectedDate(formatted);

    // default tab: poznámka (zůstává původní chování)
    setModalTab("note");

    const existing = notes.find((n) => n.date === formatted);
    setNoteText(existing ? existing.text : "");

    // reset fields pro úkol
    setTaskTitle("");
    setTaskDescription("");
    setTaskRemindAt("");

    setModalOpen(true);
  };

  const saveNote = async () => {
    if (!pharmacyCode || !selectedDate) return;

    try {
      setSaving(true);

      const res = await fetch(`/api/calendar/${pharmacyCode}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate, text: noteText }),
      });

      if (!res.ok) throw new Error("Nepodařilo se uložit poznámku.");

      const saved = await res.json();

      setNotes((prev) => {
        const exists = prev.find((n) => n.date === saved.date);
        return exists
          ? prev.map((n) => (n.date === saved.date ? saved : n))
          : [...prev, saved];
      });

      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveTask = async () => {
    if (!selectedDate) return;

    try {
      setSaving(true);
      setError("");

      const payload = {
        title: taskTitle,
        description: taskDescription,
        dueDate: `${selectedDate}T00:00:00.000Z`,
        remindAt: taskRemindAt ? `${taskRemindAt}:00` : null,
      };

      const res = await fetch("/api/tasks/my", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Nepodařilo se vytvořit úkol.");
      }

      // reload tasks, aby se hned propsaly do kalendáře
      await loadTasks();

      // zavři modal
      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async () => {
    if (!pharmacyCode || !selectedDate) return;

    try {
      setSaving(true);

      const res = await fetch(
        `/api/calendar/${pharmacyCode}?date=${selectedDate}`,
        { method: "DELETE", credentials: "include" }
      );

      if (!res.ok) throw new Error("Mazání se nepodařilo.");

      setNotes((prev) => prev.filter((n) => n.date !== selectedDate));
      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  //
  // ────────────────────────────────────────────────
  //   Render
  // ────────────────────────────────────────────────
  //
  return (
    <div className="p-6 mx-auto max-w-7xl relative">
      {/* TOOLTIP – FIXED, NEAR CURSOR */}
      <Tooltip tooltip={tooltip} cursor={cursor} />

      {/* HLAVIČKA */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
          className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          ←
        </button>

        <div className="flex flex-col items-center">
          <h2 className="text-2xl font-semibold text-lime-700">
            {format(currentMonth, "LLLL yyyy", { locale: cs })}
          </h2>
          {loading && (
            <span className="text-xs text-gray-500 mt-1">
              Načítám poznámky…
            </span>
          )}
          {error && (
            <span className="text-xs text-red-600 mt-1">{error}</span>
          )}
        </div>

        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          →
        </button>
      </div>

      {/* HLAVIČKY DNŮ */}
      <div className="grid grid-cols-7 text-center font-medium text-gray-700 mb-2">
        {["Po", "Út", "St", "Čt", "Pá", "So", "Ne"].map((d) => (
          <div key={d} className="py-2">
            {d}
          </div>
        ))}
      </div>

      {/* GRID */}
      <div className="grid grid-cols-7 border border-gray-300">
        {days.map((day, idx) => {
          const isCurrent = isToday(day);
          const inMonth = isSameMonth(day, monthStart);
          const note = getNoteForDay(day);

          const isWeekend = day.getDay() === 0 || day.getDay() === 6;

          const ts = new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate()
          ).getTime();

          const isHoliday = holidays.includes(ts);

          const bgClass = isHoliday
            ? "bg-red-50 hover:bg-red-100"
            : isWeekend
            ? "bg-gray-100 hover:bg-gray-200"
            : "bg-white hover:bg-blue-100";

          const textClass = isHoliday
            ? "text-red-900"
            : isWeekend
            ? "text-gray-900"
            : "text-black";

          const dayTasks = tasks.filter((t) => {
            const effective = getEffectiveTaskDate(t);
            if (!effective) return false;

            const tDay = format(new Date(effective), "yyyy-MM-dd");

            return (
              tDay === format(day, "yyyy-MM-dd") &&
              t.status !== "done" &&
              t.status !== "archived"
            );
          });

          const leaveEvents = getLeaveEventsForDay(day);

          const firstLeave = leaveEvents[0];

          const leaveTextClass =
            firstLeave?.status === "schváleno"
              ? "text-green-700"
              : firstLeave?.status === "čeká"
              ? "text-yellow-600"
              : "";

          return (
            <div
              key={idx}
              onClick={() => openModal(day)}
              className={`
                h-32 border border-gray-300 relative p-2 cursor-pointer transition
                ${inMonth ? "" : "bg-gray-50 text-gray-400"}
                ${isCurrent ? "border-gray-500" : ""}
                ${bgClass}
                ${inMonth ? textClass : ""}
                overflow-hidden
              `}
            >
              {/* DATUM */}
              <div className="text-sm font-semibold">
                {format(day, "d")}
              </div>

              {/* SLOTY – pevně rozdělené třetiny */}
              <div className="flex flex-col h-[calc(100%-20px)] mt-[2px] pb-[2px]">

                {/* 1) ÚKOLY — horní třetina */}
                <div className="h-1/3 flex flex-col justify-start overflow-hidden">
                  <div
                    className="flex items-center gap-1 text-[10px] leading-none truncate"
                    onMouseEnter={(e) => showTooltip(e, day, "tasks", dayTasks)}
                    onMouseLeave={hideTooltip}
                  >
                    {dayTasks.length > 0 && (
                      <>
                        <span className="text-[10px]">
                          {taskIsSnoozed(dayTasks[0]) ? "⏰" : taskHasReminder(dayTasks[0]) ? "🔔" : "📌"}
                        </span>                        
                        <span className="truncate">{dayTasks[0].title}</span>
                      </>
                    )}
                  </div>
                  <div
                    className="text-[10px] text-gray-600 truncate ml-4 mt-[1px]"
                    onMouseEnter={(e) => showTooltip(e, day, "tasks", dayTasks)}
                    onMouseLeave={hideTooltip}
                  >
                    {dayTasks.length > 1 &&
                      (dayTasks.length <= 5
                        ? `+${dayTasks.length - 1} další`
                        : `+5 dalších`)}
                  </div>
                </div>

                {/* 2) POZNÁMKA — prostřední třetina, vycentrovaná */}
                <div className="h-1/3 flex items-center overflow-hidden -mt-[4px]">
                  {note && (
                    <div
                      className="flex items-center gap-1 text-[10px] leading-none truncate"
                      onMouseEnter={(e) => showTooltip(e, day, "note", note)}
                      onMouseLeave={hideTooltip}
                    >
                      <span className="text-[10px]">📝</span>
                      <span className="truncate">{note.text}</span>
                    </div>
                  )}
                </div>

                {/* 3) UDÁLOSTI — spodní třetina */}
                <div className="h-1/3 flex flex-col justify-start overflow-hidden">
                  <div
                    className={`flex items-center gap-1 text-[10px] leading-none truncate ${
                      isApprovedLeaveStatus(leaveEvents[0]?.status)
                        ? "text-green-700"
                        : isPendingLeaveStatus(leaveEvents[0]?.status)
                        ? "text-yellow-600"
                        : ""
                    }`}                  
                    onMouseEnter={(e) => showTooltip(e, day, "leaves", leaveEvents)}
                    onMouseLeave={hideTooltip}
                  >
                    {leaveEvents.length > 0 && (
                      <>
                        <span className="text-[10px]">🗓️</span>
                        <span className="truncate">
                          {leaveEvents[0].employeeName} – {leaveEvents[0].label}
                        </span>
                      </>
                    )}
                  </div>

                  <div
                    className="text-[10px] text-gray-600 truncate ml-4 mt-[1px]"
                    onMouseEnter={(e) => showTooltip(e, day, "leaves", leaveEvents)}
                    onMouseLeave={hideTooltip}
                  >
                    {leaveEvents.length > 1 &&
                      (leaveEvents.length <= 5
                        ? `+${leaveEvents.length - 1} další`
                        : `+5 dalších`)}
                  </div>
                </div>

              </div>

            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg border border-gray-200 overflow-hidden">
            {/* HEADER */}
            <div className="px-5 pt-5 pb-3 border-b flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-gray-500">Den</div>
                <h3 className="text-lg font-semibold text-lime-700 leading-tight">
                  {fmtDateCz(selectedDate)}
                </h3>
              </div>

              {/* TABS */}
              <div className="flex items-center bg-blue-50 border border-blue-200 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setModalTab("note")}
                  className={`px-3 py-1.5 text-sm rounded-full transition ${
                    modalTab === "note"
                      ? "bg-blue-600 text-white shadow"
                      : "text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  Poznámka
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab("task")}
                  className={`px-3 py-1.5 text-sm rounded-full transition ${
                    modalTab === "task"
                      ? "bg-blue-600 text-white shadow"
                      : "text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  Úkol
                </button>
              </div>
            </div>

            {/* BODY – FIXNÍ VÝŠKA (bez poskakování) */}
            <div className="px-5 py-4 h-[300px] flex flex-col">
              {modalTab === "note" ? (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Poznámka</label>

                  {/* textarea vyplní zbytek prostoru */}
                  <textarea
                    className="
                      w-full flex-1 border border-gray-300 rounded-lg p-2 resize-none
                      focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
                    "
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Napište poznámku..."
                  />
                </>
              ) : (
                <>
                  <label className="block text-xs text-gray-600 mb-1">Název úkolu</label>
                  <input
                    className="
                      w-full border border-gray-300 rounded-lg p-2 mb-3
                      focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
                    "
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                    placeholder="Název úkolu"
                  />

                  <label className="block text-xs text-gray-600 mb-1">Popis (volitelné)</label>
                  <textarea
                    className="
                      w-full flex-1 border border-gray-300 rounded-lg p-2 resize-none
                      focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
                    "
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="Popis…"
                  />

                  <div className="mt-3">
                    <label className="block text-xs text-gray-600 mb-1">Připomenout (volitelné)</label>
                    <input
                      type="datetime-local"
                      className="
                        w-full border border-gray-300 rounded-lg p-2
                        focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
                      "
                      value={taskRemindAt}
                      onChange={(e) => setTaskRemindAt(e.target.value)}
                    />
                  </div>

                  <div className="text-xs text-gray-500 mt-2">
                    Termín: <span className="font-medium">{fmtDateCz(selectedDate)}</span>
                  </div>
                </>
              )}

              {error && <div className="text-xs text-red-600 mt-3">{error}</div>}
            </div>

            {/* FOOTER – stejné rozmístění */}
            <div className="px-5 py-4 border-t flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Zavřít
              </button>

              <div className="flex items-center gap-2">
                {modalTab === "note" && notes.find((n) => n.date === selectedDate) && (
                  <button
                    type="button"
                    onClick={deleteNote}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    Smazat
                  </button>
                )}

                <button
                  type="button"
                  onClick={modalTab === "note" ? saveNote : saveTask}
                  disabled={saving || (modalTab === "task" && !taskTitle.trim())}
                  className={`px-4 py-2 rounded-lg text-white disabled:opacity-60 ${
                    modalTab === "note"
                      ? "bg-lime-600 hover:bg-lime-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {saving
                    ? "Ukládám..."
                    : modalTab === "note"
                    ? "Uložit"
                    : "Přidat úkol"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
